import { api } from './Api'
import { ws } from './Websocket.client'
// import { loadStripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

export const isLoaded = ref<boolean>(false)

export const user = ref<User>({
  email: null,
  id: null,
  email_verified: false
} as User)

export function login(
  email: string,
  password: string
) {
  return api.post('login', {
    email: email,
    password: password,
  }).then((r: any) => {
    api.setBearer(r.data.token)

    useForEach(r.data, (v: any, k: string) => {
      user.value[k] = v
    })

    return r;
  }).finally(load)
}

export async function load() {
  const applyPatch = (data: any) => {
    if (!data) return
    useForEach(data, (v: any, k: string) => {
      user.value[k] = v
    })
  }

  const stripAdmin = (data: any) => {
    if (!data?.is_admin) {
      delete user.value.is_admin
    }
  }

  const setAnonymous = () => {
    user.value.email = null
    user.value.id = null
    user.value.name = null
  }

  const tryWebUser = async () => {
    try {
      const r: any = await api.get('web/user')
      applyPatch(r.data)
      stripAdmin(r.data)
    } catch {
      setAnonymous()
    }
  }

  const fetchFromWs = async () => {
    try {
      const authtoken = user?.value?.token ?? api?.bearerToken
      const u: any = await ws.send('auth.me', { authtoken })
      applyPatch(u)
      stripAdmin(u)
    } catch {
      setAnonymous()
      await tryWebUser()
    }
  }

  const fetchFromApi = async () => {
    try {
      const r: any = await api.get('user')
      applyPatch(r.data)
      stripAdmin(r.data)

      // Fire-and-forget: ensure websocket session picks up auth
      ws.send('auth.me')
        .then(() => {
          console.log('Websocket connected', window.performance.now())
        })
        .catch(() => {})
    } catch {
      setAnonymous()
      await tryWebUser()
    }
  }

  try {
    const wsReady = !!(ws as any)?.is_setup?.value || !!(ws as any)?.is_setup
    if (wsReady) {
      await fetchFromWs()
    } else {
      await fetchFromApi()
    }
  } finally {
    isLoaded.value = true
  }
}

export async function logout() {

  ws.send('auth.logout').finally(() => {
    user.value.email = null
    user.value.id = null
    user.value.name = null
  }).catch(() => {})

  await api.get('web/logout').finally(() => {
    user.value.email = null
    user.value.id = null
    user.value.name = null
  }).catch(() => {})

  // clear local storage bearer token
  localStorage.removeItem('bearerToken')
  sessionStorage.removeItem('bearerToken')
  api.setBearer(null)

  // is admin
  if (user.value.is_admin) {
    user.value.is_admin = false
  }

  nextTick(() => {
    useRouter().push(useLocalePath()('/login'))
  })
}

export function save() {
  if (!ws.is_setup.value) {
    return api.put('user', user.value)
      .then((res) => {
        notify('Details saved')
        return res;
      }).catch(api.parseError)
  } else {
    return ws.send('auth.update', user.value)
      .then((res) => {
        notify('Details saved')
        return res;
      }).catch(api.parseError)
  }
}

export function setting(setting: string, value?: any) {
  return ws.send('auth.setting', {
    setting,
    value
  })
}

export const isAuthed = computed<boolean>(() => {
  return !!(user.value.id && user.value.email)
})

export const guestPages = [
  '/',
  '/login',
  '/register',
  '/privacy',
  '/warmingup',
  '/pricing',
]

export const loadFromSocialite = async(token: string) => {
  return ws.send('app.bearerFromSocialToken',{token}).then((r) => {
    if (r?.token) {
      api.setBearer(r.token)
      localStorage.setItem('bearerToken', r.token)
      load()
    }else{
      console.warn('No token received from socialite login')
    }
  })
}

export const methodResendConfirmation = () => {
  api.post('user/resend-mail').then((r) => {
  }).catch(api.parseError)

  notify(useT('resendconfirmationsent'))
}


watch(isAuthed, (v) => {
  if (v) {
    useState<boolean | null>('isConsentGiven', () => null).value = true

    const path = useRoute().path

    ws.send('app.stripekey').then((key) => {
      loadStripe(key).then((stripe) => {
        useStripe().value = stripe
        window.stripe = useStripe().value
      })
    })

    if (
      path.includes('/login')
      || path.includes('/register')
      || (useRoute()?.name as string)?.startsWith('index___')
    ) {
      useRouter().push(useLocalePath()('/home'))
    }
  }
})
